// recording-orb.tsx
import React from 'react';

type Props = {
  listening?: boolean;
  /** Primary shadow/fill color (used as a fallback) */
  color?: string;
  /** Background color to fade into. Defaults to computed body background or '#070a12'. */
  backgroundColor?: string;
  /** Ignored (kept for API compatibility). */
  palette?: string[];
  /** Max layer count */
  layers?: number;
  /** Envelope EMA (lower = snappier) */
  smoothing?: number;
  /** Mic envelope gain (higher = more sensitive) */
  gain?: number;
  /** Base waves across width */
  frequency?: number;
  /**
   * ✅ NEW:
   * Controls how many “big” wave cycles appear across the full screen width.
   * Lower value = fewer waveforms on screen.
   * If omitted, the existing `frequency` behavior is used (no change).
   */
  waveCount?: number;
  /** Base horizontal motion */
  speed?: number;
  /** Subtle breathing rate */
  pulseRate?: number;
  /** Baseline amplitude when quiet */
  minAmplitude?: number;
  /** Ignored */
  opacityRange?: [number, number];
  /** Blend mode for “moving shadows” look */
  blendMode?: GlobalCompositeOperation;
  /** Blur radius for soft shadow feel */
  shadowBlur?: number;
  /** Always uses gradient now; left for API compatibility */
  useGradient?: boolean;
  className?: string;

  /** Organic variation controls */
  variation?: {
    octaves?: number;
    jitterScale?: number;
    warp?: number;
    perLayerFreqJitter?: number;
    perLayerPhaseDrift?: number;
    verticalWobble?: number;
  };
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};

// Tiny seeded PRNG
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// 2D Perlin-ish noise + fBm
function makeNoise2D(seed: number) {
  const rnd = mulberry32(seed);
  const perm = new Uint8Array(512);
  for (let i = 0; i < 256; i++) perm[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = (rnd() * (i + 1)) | 0;
    [perm[i], perm[j]] = [perm[j], perm[i]];
  }
  for (let i = 0; i < 256; i++) perm[i + 256] = perm[i];

  const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const grad = (h: number, x: number, y: number) => {
    const u = h & 1 ? -x : x;
    const v = h & 2 ? -y : y;
    return u + v;
  };

  function perlin(x: number, y: number) {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    const xf = x - Math.floor(x);
    const yf = y - Math.floor(y);
    const u = fade(xf);
    const v = fade(yf);

    const aa = perm[X + perm[Y]]!;
    const ab = perm[X + perm[Y + 1]]!;
    const ba = perm[X + 1 + perm[Y]]!;
    const bb = perm[X + 1 + perm[Y + 1]]!;

    const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
    const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);
    return lerp(x1, x2, v); // ~[-1,1]
  }

  return function fbm(x: number, y: number, octaves = 3, lacunarity = 2.0, gain = 0.5) {
    let amp = 1;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += perlin(x * freq, y * freq) * amp;
      norm += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    return sum / (norm || 1);
  };
}

type AudioBundle = {
  ctx: AudioContext;
  analyser: AnalyserNode;
  src: MediaStreamAudioSourceNode;
  stream: MediaStream;
  data: Uint8Array;
  fft: Uint8Array;
  hp: BiquadFilterNode;
  lp: BiquadFilterNode;
  comp: DynamicsCompressorNode;
  preGain: GainNode;
};

function readBackgroundFallback(): string {
  try {
    const body = getComputedStyle(document.body);
    const bg = body.backgroundColor;
    return bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#070a12';
  } catch {
    return '#070a12';
  }
}

/**
 * Add more blur, like a mask over the whole thing;
 * make waves shorter
 */
export default function FullscreenWaveform({
  listening = true,
  color = '#2a66ff',
  backgroundColor,
  layers = 8,
  smoothing = 0.72,
  gain = 2.1,
  frequency = 1.1,
  waveCount,
  speed = 0.9,
  pulseRate = 0.6,
  minAmplitude = 0.06,
  blendMode = 'screen',
  shadowBlur = 26,
  useGradient = true,
  className = '',
  variation = {},
}: Props) {
  const {
    octaves = 4,
    jitterScale = 1.25,
    warp = 44,
    perLayerFreqJitter = 0.55,
    perLayerPhaseDrift = 0.7,
    verticalWobble = 0.12,
  } = variation;

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  const rafRef = React.useRef<number | null>(null);
  const lastTRef = React.useRef<number | null>(null);

  // phases
  const phaseRef = React.useRef(0);
  const pulsePhaseRef = React.useRef(0);

  // audio envelope
  const envRef = React.useRef(0);

  // idling shimmer envelope (keeps movement when quiet)
  const idleRef = React.useRef(0);

  const seedsRef = React.useRef<number[]>([]);
  const noiseRef = React.useRef<((x: number, y: number, o?: number) => number)[]>([]);

  const audioRef = React.useRef<AudioBundle | null>(null);
  const pausedRef = React.useRef(false);

  const bgColorRef = React.useRef<string>('#070a12');

  // ✅ NEW: keep accurate CSS-size + DPR so the canvas always renders full-width on mobile
  const dprRef = React.useRef(1);
  const cssSizeRef = React.useRef({ w: 1, h: 1 });

  // ✅ Metaball fusion buffers (offscreen)
  const fusionDrawCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const fusionBlurCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const fusionScaleRef = React.useRef(0.55); // lower = faster (keeps look “metaball”)

  const cleanup = React.useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.stream.getTracks().forEach((t) => t.stop());
      } catch {}
      try {
        audioRef.current.ctx.close();
      } catch {}
      audioRef.current = null;
    }
  }, []);

  // Pause when hidden
  React.useEffect(() => {
    const onVis = () => (pausedRef.current = document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Background color detect (for fades)
  React.useEffect(() => {
    bgColorRef.current = backgroundColor || readBackgroundFallback();
  }, [backgroundColor]);

  // Per-layer seeds & noise
  React.useEffect(() => {
    const n = Math.max(1, Math.floor(layers));
    const seeds = Array.from({ length: n }, (_, i) => (i + 1) * 1234567);
    const noises = seeds.map((s) => makeNoise2D(s));
    seedsRef.current = seeds;
    noiseRef.current = noises;
  }, [layers]);

  const ensureFusionCanvases = React.useCallback(() => {
    if (!fusionDrawCanvasRef.current)
      fusionDrawCanvasRef.current = document.createElement('canvas');
    if (!fusionBlurCanvasRef.current)
      fusionBlurCanvasRef.current = document.createElement('canvas');
  }, []);

  const resizeCanvasToElement = React.useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;

    const rect = c.getBoundingClientRect();
    const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));

    // Store CSS size + DPR so draw() uses consistent units everywhere (mobile-safe)
    cssSizeRef.current = { w: Math.max(1, rect.width), h: Math.max(1, rect.height) };
    dprRef.current = dpr;

    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));

    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;

    const ctx = c.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // ✅ resize fusion buffers (scaled)
    ensureFusionCanvases();
    const s = fusionScaleRef.current;
    const fw = Math.max(1, Math.floor(rect.width * dpr * s));
    const fh = Math.max(1, Math.floor(rect.height * dpr * s));

    const f1 = fusionDrawCanvasRef.current!;
    const f2 = fusionBlurCanvasRef.current!;
    if (f1.width !== fw) f1.width = fw;
    if (f1.height !== fh) f1.height = fh;
    if (f2.width !== fw) f2.width = fw;
    if (f2.height !== fh) f2.height = fh;
  }, [ensureFusionCanvases]);

  const makeBlueRibbonGradient = React.useCallback(
    (ctx: CanvasRenderingContext2D, width: number, layerIndex: number, tMs: number) => {
      // horizontal multi-stop gradient (a “rainbow” but only blue hues)
      // animate stops slightly so color breathes, not uniform blocks
      const g = ctx.createLinearGradient(0, 0, width, 0);

      const tt = tMs * 0.00008 + layerIndex * 0.17;
      const wob = (a: number) => 0.5 + 0.5 * Math.sin(tt + a);

      // deep → electric → cyan → indigo → deep
      const a0 = 0.22 + 0.18 * wob(0.0);
      const a1 = 0.24 + 0.18 * wob(1.1);
      const a2 = 0.18 + 0.16 * wob(2.2);
      const a3 = 0.2 + 0.16 * wob(3.3);
      const a4 = 0.2 + 0.16 * wob(4.4);

      // slight per-layer brightness shift
      const boost = clamp(0.78 + layerIndex * 0.04, 0.78, 1.05);

      const rgba = (r: number, gg: number, b: number, a: number) =>
        `rgba(${Math.round(r * boost)},${Math.round(gg * boost)},${Math.round(b * boost)},${a})`;

      g.addColorStop(0.0, rgba(20, 80, 255, a0));
      g.addColorStop(0.18, rgba(55, 145, 255, a1));
      g.addColorStop(0.45, rgba(95, 215, 255, a2));
      g.addColorStop(0.72, rgba(65, 115, 255, a3));
      g.addColorStop(1.0, rgba(22, 70, 255, a4));

      return g;
    },
    []
  );

  const draw = React.useCallback(
    (t: number) => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      if (!ctx) return;

      ensureFusionCanvases();
      const fusionDrawCanvas = fusionDrawCanvasRef.current!;
      const fusionBlurCanvas = fusionBlurCanvasRef.current!;
      const fctx = fusionDrawCanvas.getContext('2d');
      const bctx = fusionBlurCanvas.getContext('2d');
      if (!fctx || !bctx) return;

      const last = lastTRef.current ?? t;
      const dt = Math.min(0.05, (t - last) / 1000);
      lastTRef.current = t;

      // ✅ Use stored CSS size so mobile always fills screen width correctly
      const width = cssSizeRef.current.w;
      const height = cssSizeRef.current.h;
      const bg = bgColorRef.current;

      if (pausedRef.current) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      // --- AUDIO ANALYSIS (continuous, no on/off snapping) ---
      let env = envRef.current;
      let bass = 0,
        mids = 0,
        highs = 0;

      const audio = audioRef.current;
      if (audio) {
        audio.analyser.getByteTimeDomainData(audio.data as unknown as Uint8Array<ArrayBuffer>);
        audio.analyser.getByteFrequencyData(audio.fft as unknown as Uint8Array<ArrayBuffer>);

        // RMS
        let sum = 0;
        const n = audio.data.length;
        for (let i = 0; i < n; i++) {
          const v = (audio.data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / n);

        // EMA
        env = smoothing * env + (1 - smoothing) * rms;
        envRef.current = env;

        // Bands
        const fft = audio.fft;
        const len = fft.length;
        const bEnd = Math.max(1, (len * 0.12) | 0);
        const mEnd = Math.max(bEnd + 1, (len * 0.38) | 0);

        let sb = 0,
          sm = 0,
          sh = 0;
        for (let i = 0; i < bEnd; i++) sb += fft[i];
        for (let i = bEnd; i < mEnd; i++) sm += fft[i];
        for (let i = mEnd; i < len; i++) sh += fft[i];

        bass = sb / (bEnd || 1) / 255;
        mids = sm / (mEnd - bEnd || 1) / 255;
        highs = sh / (len - mEnd || 1) / 255;
      } else {
        // no mic (or permissions denied): keep idle only
        env = smoothing * env + (1 - smoothing) * 0;
        envRef.current = env;
      }

      // Always-on idle shimmer amplitude (even in silence)
      // This prevents “min/max only” visuals and gives you a premium baseline.
      const idle = idleRef.current;
      const idleTarget =
        0.012 +
        0.01 * (0.5 + 0.5 * Math.sin(t * 0.0013)) +
        0.006 * (0.5 + 0.5 * Math.sin(t * 0.00077 + 1.2));
      idleRef.current = idle + (idleTarget - idle) * (1 - Math.exp(-dt / 0.35));

      // Map RMS -> a nicer “audio meter” curve (log-ish)
      // Typical speech RMS is small; this makes mid-range visible.
      const raw = env * gain;
      const loud01 = clamp(Math.log10(1 + raw * 18) / Math.log10(1 + 18), 0, 1);

      // Keep some baseline even when quiet, but still reactive
      const baseActivity = clamp(idle * 7.0, 0, 0.18);
      const activity = clamp(baseActivity + loud01, 0, 1);

      // Smooth “strength” (prevents jitter)
      const strength = smoothstep(0.02, 0.75, activity);

      // Phases (RIGHT → LEFT motion = negative phase travel)
      phaseRef.current -= 2 * Math.PI * (speed * 0.55) * dt;
      pulsePhaseRef.current += 2 * Math.PI * (pulseRate * 0.45) * dt;
      const basePhase = phaseRef.current;

      const breathe = 0.86 + 0.14 * (0.5 + 0.5 * Math.sin(pulsePhaseRef.current));

      // Clear main
      const dpr = dprRef.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      // ✅ Clear fusion buffers
      fctx.setTransform(1, 0, 0, 1, 0, 0);
      fctx.clearRect(0, 0, fusionDrawCanvas.width, fusionDrawCanvas.height);
      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.clearRect(0, 0, fusionBlurCanvas.width, fusionBlurCanvas.height);

      // We draw the FILLS into fusion buffer, then metaball-fuse them.
      // Crest shimmer is still drawn on main ctx (unchanged behavior).
      fctx.globalCompositeOperation = 'source-over';
      fctx.globalAlpha = 1;
      fctx.shadowBlur = 0;
      fctx.shadowColor = 'rgba(0,0,0,0)';

      // Render config
      const maxLayers = Math.max(1, Math.floor(layers));
      const activeLayers = maxLayers; // always render, but depth scales with strength
      const points = Math.min(1200, Math.max(720, Math.floor(width * 1.1))); // responsive fidelity

      // Top-origin baseline + anti-cutoff guards
      const topMargin = Math.max(10, height * 0.02);
      const baseYTop = topMargin;

      // Depth goes DOWN from top (this is what you asked)
      const maxDepth = height * (0.18 + 0.62 * strength) * breathe;

      // Fade to background at the TOP so it never looks cut off at the origin
      // (since our wave now “starts” at the top)
      const topFade = (ctx2: CanvasRenderingContext2D, depthPx: number) => {
        const g = ctx2.createLinearGradient(0, 0, 0, Math.max(1, depthPx));
        g.addColorStop(0.0, bg);
        g.addColorStop(0.25, bg);
        g.addColorStop(0.75, 'rgba(0,0,0,0)');
        g.addColorStop(1.0, 'rgba(0,0,0,0)');
        return g;
      };

      // ✅ Scale mapping main CSS coords -> fusion buffer coords
      const fx = fusionDrawCanvas.width / Math.max(1, width * dpr);
      const fy = fusionDrawCanvas.height / Math.max(1, height * dpr);

      // Collect crest paths to draw shimmer exactly as before (on main ctx)
      const crestPaths: {
        path: Path2D;
        baseY: number;
        layerAmp: number;
        k: number;
        L: number;
      }[] = [];

      for (let L = activeLayers - 1; L >= 0; L--) {
        const k = activeLayers === 1 ? 0 : L / (activeLayers - 1); // back->front 0..1
        const rnd = mulberry32(seedsRef.current[L] || (L + 1) * 97);
        const n2 = noiseRef.current[L] || ((() => 0) as any);

        // per-layer variation
        const freqJ = 1 + (rnd() * 2 - 1) * (perLayerFreqJitter * 0.24);
        const drift = (rnd() * 2 - 1) * (perLayerPhaseDrift * 0.18);

        // band-aware “energy”
        const bandMix = 0.6 * bass + 0.28 * mids + 0.12 * highs;

        // layer depth and amplitude (downward)
        const layerDepth =
          (0.35 + 0.65 * k) *
          (0.7 + 0.85 * bandMix) *
          (0.55 * maxDepth + 0.45 * maxDepth * breathe);

        const layerAmp = Math.max(layerDepth * 1.15, height * minAmplitude);

        // baseline wobble (slight vertical drift at the top)
        const wob =
          height *
          verticalWobble *
          0.14 *
          (n2(0.12 + L * 0.2, t * 0.00045 + L * 0.33, octaves) ?? 0);
        const baseY = baseYTop + wob;

        // Build “blue rainbow” gradient per layer (not uniform)
        // NOTE: gradient built in main ctx for consistency; we approximate on fusion ctx by using same stops.
        // (We create it on fusion ctx with mapped width so colors stay animated.)
        const ribbon = makeBlueRibbonGradient(fctx as any, fusionDrawCanvas.width, L, t);

        // If useGradient: also mix in a top fade into bg so the top edge is seamless
        // We’ll do this by drawing the layer, then overlaying a top fade with multiply-ish.
        const fillStyle = ribbon;

        // Draw fill into fusion buffer
        fctx.save();
        fctx.beginPath();

        // Start at TOP-LEFT (since we fill DOWN)
        fctx.moveTo(0, 0);
        fctx.lineTo(0, baseY * fy);

        // ✅ NEW: optional explicit control of “how many waveforms” across the width
        const baseWaves = waveCount ?? frequency * 0.88;
        const omega = 2 * Math.PI * baseWaves * freqJ;

        const localPhase = basePhase * (1 + 0.22 * (1 - k)) + drift * t * 0.0017 + k * 1.05;

        const crestPath = new Path2D();
        const depthLimit = height - 2; // keep within canvas

        for (let i = 0; i <= points; i++) {
          const x = (i / points) * width;

          // Domain warp: breaks sine regularity
          const warpX =
            warp *
            (0.62 + 0.85 * strength) *
            (n2((x + 1000) * 0.0022, (t * 0.00125 + L) * 0.65, octaves) || 0);

          const warped = (x + warpX) / width;

          // core wave (slightly asymmetric for “premium” motion)
          const s = Math.sin(omega * warped + localPhase);
          const asymmetric = Math.sign(s) * Math.pow(Math.abs(s), 0.68);

          // harmonic + shimmer curvature
          const harmonic =
            0.22 * (0.35 + strength) * Math.sin(2.0 * omega * warped - localPhase * 0.62 + L);
          const curv =
            jitterScale *
            0.3 *
            (0.8 + 0.45 * strength) *
            (n2(warped * 4.4 + L * 0.5, t * 0.00062 + k * 0.42, octaves) || 0);

          // amplitude goes DOWN from the top baseline
          const amp = layerAmp * (0.52 + 0.48 * (asymmetric + harmonic)) + curv * 22;

          let y = baseY + amp;

          // soft floor guard (no harsh clipping)
          if (y > depthLimit) {
            const over = y - depthLimit;
            y = depthLimit - over * 0.25;
          }

          // fusion fill path
          fctx.lineTo(x * fx * dpr, y * fy * dpr);

          // crest shimmer path (in MAIN coords)
          if (i === 0) crestPath.moveTo(x, y);
          else crestPath.lineTo(x, y);
        }

        // Close to TOP-RIGHT
        fctx.lineTo(fusionDrawCanvas.width, baseY * fy);
        fctx.lineTo(fusionDrawCanvas.width, 0);
        fctx.closePath();

        fctx.fillStyle = fillStyle as any;
        fctx.fill();

        // Top fade overlay (so the origin melts into bg)
        if (useGradient) {
          fctx.save();
          fctx.globalCompositeOperation = 'multiply';
          fctx.globalAlpha = 0.75;
          fctx.fillStyle = topFade(fctx as any, Math.max(90, layerAmp * 0.9) * fy) as any;
          fctx.fillRect(0, 0, fusionDrawCanvas.width, Math.max(110, layerAmp) * fy);
          fctx.restore();
        }

        fctx.restore();

        crestPaths.push({ path: crestPath, baseY, layerAmp, k, L });
      }

      // ✅ METABALL FUSION (blur + alpha threshold) on fusion buffers
      // - blur makes nearby shapes “connect”
      // - threshold turns soft blobs into fused solids
      {
        const fw = fusionDrawCanvas.width;
        const fh = fusionDrawCanvas.height;

        // Blur pass
        bctx.save();
        bctx.clearRect(0, 0, fw, fh);

        // Blur amount scales with your shadowBlur + strength (so it fuses more when “active”)
        const blurPx = clamp(6 + shadowBlur * 0.35 + strength * 10, 6, 26);
        (bctx as any).filter = `blur(${blurPx}px)`;
        bctx.drawImage(fusionDrawCanvas, 0, 0);
        (bctx as any).filter = 'none';
        bctx.restore();

        // Threshold pass (in-place on blur canvas)
        const img = bctx.getImageData(0, 0, fw, fh);
        const data = img.data;

        // Alpha threshold controls “stickiness”
        const thresh = clamp(70 + (1 - strength) * 25, 55, 110); // lower = more fusion
        // Optional soft edge via a tiny alpha ramp
        const knee = 26;

        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];

          if (a <= thresh - knee) {
            data[i + 3] = 0;
          } else if (a >= thresh + knee) {
            data[i + 3] = 255;
          } else {
            // smooth ramp in knee region
            const t2 = (a - (thresh - knee)) / (2 * knee);
            data[i + 3] = Math.round(255 * (t2 * t2 * (3 - 2 * t2)));
          }
        }

        bctx.putImageData(img, 0, 0);

        // Draw fused result onto main canvas (keep your existing blendMode + shadow)
        ctx.save();
        ctx.globalCompositeOperation = blendMode;
        ctx.globalAlpha = 1;
        ctx.shadowBlur = shadowBlur;
        ctx.shadowColor = color;

        // Scale fused buffer to main CSS size
        ctx.drawImage(fusionBlurCanvas, 0, 0, width, height);
        ctx.restore();
      }

      // --- Shimmer lines (crest glows + high-end sparkle) ---
      // (kept as-is, using the same crest paths computed above)
      for (let idx = crestPaths.length - 1; idx >= 0; idx--) {
        const { path: crestPath, baseY, layerAmp, k, L } = crestPaths[idx];

        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.globalAlpha = 0.1 + 0.12 * strength;

        const glowGrad = ctx.createLinearGradient(
          0,
          baseY,
          0,
          clamp(baseY + layerAmp * 0.9, 0, height)
        );
        glowGrad.addColorStop(0, 'rgba(70,120,255,0.0)');
        glowGrad.addColorStop(0.35, 'rgba(140,195,255,0.55)');
        glowGrad.addColorStop(1, 'rgba(150,220,255,0.0)');

        ctx.strokeStyle = glowGrad;
        ctx.lineWidth = 1.2 + 0.7 * (1 - k);
        ctx.stroke(crestPath);

        if (highs > 0.015) {
          const sparkleAlpha = clamp(0.05 + highs * 0.4 + strength * 0.12, 0.06, 0.33);
          ctx.globalAlpha = sparkleAlpha;
          ctx.lineWidth = 0.75;

          const dash = 10 + 42 * (0.25 + highs * 0.75);
          const gap = 8 + 34 * (0.25 + highs * 0.75);
          (ctx as any).setLineDash?.([dash, gap]);
          (ctx as any).lineDashOffset = -t * 0.07 - L * 7;

          ctx.strokeStyle = 'rgba(210,235,255,0.95)';
          ctx.stroke(crestPath);

          (ctx as any).setLineDash?.([]);
        }
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame((ts: number) => drawRef.current(ts));
    },
    [
      gain,
      smoothing,
      frequency,
      waveCount,
      speed,
      pulseRate,
      minAmplitude,
      layers,
      blendMode,
      shadowBlur,
      useGradient,
      color,
      octaves,
      jitterScale,
      warp,
      perLayerFreqJitter,
      perLayerPhaseDrift,
      verticalWobble,
      makeBlueRibbonGradient,
      ensureFusionCanvases,
    ]
  );

  // ✅ Always point to the latest draw — the RAF loop calls through this ref so
  // it never gets stuck running stale params when waveformProps change mid-transition.
  const drawRef = React.useRef(draw);
  drawRef.current = draw;

  React.useEffect(() => {
    if (!listening) {
      cleanup();
      return;
    }

    let cancelled = false;

    const c = canvasRef.current;
    if (!c) return;

    // Responsive: ResizeObserver (fixes “different screens show different things”)
    const ro = new ResizeObserver(() => resizeCanvasToElement());
    ro.observe(c);

    (async () => {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

      const AC: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;

      const ctx: AudioContext = new AC();
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch {}
      }

      try {
        // For visuals, the browser’s built-in processing can help stability,
        // so we allow it unless you *specifically* want raw mic.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const src = ctx.createMediaStreamSource(stream);

        // Pre-gain (lets you tune sensitivity without fighting AGC)
        const preGain = ctx.createGain();
        preGain.gain.value = 1.0;

        // HP → LP → Compressor → Analyser
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 85;

        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 5200;

        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -34;
        comp.knee.value = 22;
        comp.ratio.value = 5.5;
        comp.attack.value = 0.004;
        comp.release.value = 0.14;

        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.86;

        src.connect(preGain);
        preGain.connect(hp);
        hp.connect(lp);
        lp.connect(comp);
        comp.connect(analyser);

        const data = new Uint8Array(analyser.fftSize);
        const fft = new Uint8Array(analyser.frequencyBinCount);

        audioRef.current = { ctx, analyser, src, stream, data, fft, hp, lp, comp, preGain };
      } catch (err) {
        console.warn('Mic unavailable; running idle-only waveform.', err);
        audioRef.current = null;
      }

      resizeCanvasToElement();
      rafRef.current = requestAnimationFrame((ts: number) => drawRef.current(ts));
    })().catch((err) => console.error('Mic error:', err));

    return () => {
      cancelled = true;
      ro.disconnect();
      cleanup();
    };
  }, [listening]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      style={{
        position: 'relative',
        inset: 0,
        width: '100vw',
        height: '50vh',
        display: 'block',
        pointerEvents: 'none',
        zIndex: '1',
      }}
    />
  );
}
